import React, { FC, useState } from 'react';
import { usePermissions, useTranslate, crudGetOne } from 'react-admin';
import { EditButton, Drawer } from '~/components';
import { EditForm } from '~/components/ra';
import onError from '~/errorsHandler';
import MenuForm from './MenuForm';
import { TreeNode } from '~/types/Menu';
import ChildForm from './ChildForm';
import { queryClient } from '~/App';
import { useDispatch } from 'react-redux';

const l = (key: string): string => `resources.menu.edit.${key}`;

interface EditDrawerProps {
  selectedNode: TreeNode | null;
  selectedType: 'menu' | 'menuItem';
  menuId: string;
  open: boolean;
  onClose: () => void;
  parentId?: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
  refetch?: () => Promise<any>;
}

const EditDrawer: FC<EditDrawerProps> = (props) => {
  const {
    selectedNode,
    open,
    onClose,
    menuId,
    selectedType,
    parentId,
    loadChildren,
    refetch,
  } = props;

  const handleError = (error: any) => {
    onError(error);
  };

  const handleClose = () => {
    onClose();
  };

  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const { permissions } = usePermissions();
  if (!selectedNode) return null;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      heading={selectedType === 'menu' ? t('title') : t('titleItem')}
    >
      <EditForm
        resource='menu'
        id={selectedType === 'menuItem' ? `${menuId}/items/${selectedNode?.id}` : selectedNode?.id}
        record={selectedNode}
        closeParent={onClose}
        onSuccess={() => {
          if (selectedType === 'menu' && !refetch) {
            dispatch(crudGetOne('menu', menuId, '/menu'));
          } else if (selectedType === 'menuItem' && !refetch) {
            if (parentId && parentId !== menuId && loadChildren) {
              loadChildren?.(parentId, true);
            } else {
              queryClient.invalidateQueries({
                queryKey: ['menu', menuId, 'items', '$'],
              });
            }
          } else if (refetch) {
            refetch();
          }
          handleClose();
        }}
        onError={handleError}
        deleteButtonProps={{
          variant: 'delete',
          disabled: !permissions?.includes('dynamicMenu.delete'),
        }}
        disabled={!permissions?.includes('profileTag.update')}
        parseRecordBeforeSubmit={(item) => {
          if (selectedType === 'menu') {
            return {
              ...item,
              parentId: null,
              type: null,
            };
          } else if (selectedType === 'menuItem') {
            const transformedItem = {
              ...item,
              parentId: menuId === item?.parentId ? null : item?.parentId,
              type: null,
              name: null,
            };
            return transformedItem;
          } else {
            return item;
          }
        }}
      >
        {selectedType === 'menu' && <MenuForm edit />}
        {selectedType === 'menuItem' && <ChildForm edit menuId={menuId} />}
      </EditForm>
    </Drawer>
  );
};

interface EditActionProps {
  selectedNode: TreeNode | null;
  selectedType: 'menu' | 'menuItem';
  menuId: string;
  parentId?: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
  refetch?: () => Promise<any>;
}

const EditAction: FC<EditActionProps> = (props) => {
  const { selectedNode, selectedType, menuId, parentId, loadChildren, refetch } = props;
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { permissions } = usePermissions();
  if (!permissions?.includes('dynamicMenu.update')) return null;
  if (!selectedNode) return null;
  return (
    <>
      <EditButton onClick={handleOpen} record={selectedNode} />
      <EditDrawer
        open={open}
        onClose={handleClose}
        selectedNode={selectedNode}
        selectedType={selectedType}
        menuId={menuId}
        parentId={parentId}
        loadChildren={loadChildren}
        refetch={refetch}
      />
    </>
  );
};

export default EditAction;
